import SeloSituacao from '@/components/feature/SeloSituacao';
import { formatarValor } from '@/pages/evento/utils';
import { formatarDataCurta, formatarDinheiro } from '../utils';
import { ordinal } from '../vagas';
import type { FilaLinha, InfoLinhaVagas, InscritoGerenciar } from '../types';

interface Props {
  inscritos: InscritoGerenciar[];
  saldoMap: Map<number, number>;
  temFoto: boolean;
  fotoUrlMap: Map<number, string>;
  mostrarParticipacao: boolean;
  /** Equipe de serviço de cada pessoa (aba Equipe do acampamento). */
  equipeMap?: Map<number, string>;
  /** Senha e vaga/suplente (aba Campistas do acampamento). */
  filaMap?: Map<number, FilaLinha>;
  /** Vaga, posição na fila, subida e cancelamento. */
  vagasMap?: Map<number, InfoLinhaVagas>;
  /** Seleção em lote (imprimir fichas). */
  selecionados?: Set<number>;
  aoAlternarSelecao?: (inscricaoId: number) => void;
  /** Linha saindo (cancelou / subiu): encolhe a altura antes da releitura. */
  saindoId?: number | null;
  aoAbrir: (inscrito: InscritoGerenciar) => void;
}

const FILA_ROTULO: Record<string, string> = {
  vaga: 'Vaga',
  suplente: 'Suplente',
  aguardando: 'Aguardando',
};

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  const primeira = partes[0]?.[0] ?? '';
  const segunda = partes.length > 1 ? partes[1][0] : '';
  return (primeira + segunda).toUpperCase();
}

export default function TabelaInscritos({
  inscritos,
  saldoMap,
  temFoto,
  fotoUrlMap,
  mostrarParticipacao,
  equipeMap,
  filaMap,
  vagasMap,
  selecionados,
  aoAlternarSelecao,
  saindoId = null,
  aoAbrir,
}: Props) {
  const comSelecao = !!aoAlternarSelecao;
  return (
    <div className="overflow-hidden rounded-3xl bg-white shadow-[0_6px_18px_rgba(20,25,60,.06)]">
      <table className="w-full text-left">
        <thead>
          <tr className="text-[12px] uppercase tracking-wide text-foreground-500">
            {comSelecao && <th className="w-10 px-2 py-3 font-medium"> </th>}
            <th className="px-4 py-3 font-medium">Participante</th>
            {mostrarParticipacao && <th className="px-4 py-3 font-medium">Participação</th>}
            {equipeMap && <th className="px-4 py-3 font-medium">Equipe</th>}
            {filaMap && <th className="px-4 py-3 font-medium">Senha</th>}
            <th className="px-4 py-3 font-medium">Grupo</th>
            <th className="px-4 py-3 font-medium">Telefone</th>
            <th className="px-4 py-3 font-medium">Inscrito em</th>
            <th className="px-4 py-3 font-medium">Situação</th>
            <th className="px-4 py-3 text-right font-medium">Valor</th>
            <th className="px-4 py-3 text-right font-medium">Saldo</th>
          </tr>
        </thead>
        <tbody>
          {inscritos.map((i) => {
            const cancelada = i.status === 'cancelada';
            const saldo = saldoMap.get(i.inscricao_id) ?? null;
            const fila = filaMap?.get(i.inscricao_id);
            const suplente = i.status === 'lista_espera' || fila?.situacao === 'suplente';
            const classeLinha = cancelada ? 'text-foreground-400' : 'text-foreground-900';
            const saldoCor = cancelada || suplente
              ? classeLinha
              : saldo === null
                ? classeLinha
                : saldo > 0
                  ? 'font-semibold text-amber-700'
                  : 'text-accent-700';

            return (
              <tr
                key={i.inscricao_id}
                onClick={() => aoAbrir(i)}
                className={`cursor-pointer border-t border-secondary-100 transition-all duration-200 [&>td]:transition-[padding,border] [&>td]:duration-200 hover:bg-primary-50 ${
                  saindoId === i.inscricao_id ? 'opacity-0 [&>td]:!border-0 [&>td]:!py-0' : ''
                }`}
              >
                {comSelecao && (
                  <td className="px-2 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <label className="flex cursor-pointer items-center justify-center">
                      <input
                        type="checkbox"
                        checked={selecionados?.has(i.inscricao_id) ?? false}
                        onChange={() => aoAlternarSelecao?.(i.inscricao_id)}
                        aria-label={`Selecionar ${i.participante}`}
                        className="h-4 w-4 cursor-pointer accent-primary-600"
                      />
                    </label>
                  </td>
                )}
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-3">
                    {temFoto && (
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-100 text-[13px] font-bold text-primary-700">
                        {fotoUrlMap.has(i.inscricao_id) ? (
                          <img
                            src={fotoUrlMap.get(i.inscricao_id)}
                            alt={`Foto de ${i.participante}`}
                            className="h-full w-full object-contain object-center"
                          />
                        ) : (
                          iniciais(i.participante)
                        )}
                      </span>
                    )}
                    <span className="min-w-0">
                      <span
                        className={`block text-[15px] font-semibold ${
                          cancelada ? 'line-through' : ''
                        } ${classeLinha}`}
                      >
                        {i.participante}
                      </span>
                      {(() => {
                        const v = vagasMap?.get(i.inscricao_id);
                        const motivo =
                          cancelada && v?.cancelamento_motivo
                            ? [v.cancelamento_motivo, v.cancelamento_detalhe]
                                .filter(Boolean)
                                .join(' · ')
                            : null;
                        const posicao = v?.posicao ? `Suplente ${ordinal(v.posicao)}` : null;
                        // Suplente ainda sem senha validada na portaria.
                        const naoValidado =
                          i.status === 'lista_espera' && v && !v.validada && !v.posicao
                            ? 'não validado'
                            : null;
                        if (!v?.subiu_em && !motivo && !posicao && !naoValidado) return null;
                        return (
                          <span className="mt-1 flex flex-wrap items-center gap-1">
                            {posicao && (
                              <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-semibold text-secondary-800">
                                {posicao}
                              </span>
                            )}
                            {naoValidado && (
                              <span className="rounded-full bg-background-100 px-2 py-0.5 text-[11px] font-semibold text-foreground-600">
                                {naoValidado}
                              </span>
                            )}
                            {v?.subiu_em && (
                              <span className="rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-semibold text-accent-900">
                                subiu de suplente em {formatarDataCurta(v.subiu_em)}
                              </span>
                            )}
                            {motivo && (
                              <span className="rounded-full bg-background-100 px-2 py-0.5 text-[11px] font-medium text-foreground-600">
                                {motivo}
                                {v?.cancelada_em ? ` · ${formatarDataCurta(v.cancelada_em)}` : ''}
                              </span>
                            )}
                          </span>
                        );
                      })()}
                    </span>
                  </div>
                </td>
                {mostrarParticipacao && (
                  <td className={`px-4 py-2.5 text-[14px] ${classeLinha}`}>{i.modalidade}</td>
                )}
                {equipeMap && (
                  <td className={`px-4 py-2.5 text-[14px] ${classeLinha}`}>
                    {equipeMap.get(i.inscricao_id) || '—'}
                  </td>
                )}
                {filaMap && (
                  <td className={`px-4 py-2.5 text-[14px] ${classeLinha}`}>
                    {fila?.senha ? (
                      <span className="flex flex-col leading-tight">
                        <strong className="font-semibold">{fila.senha}</strong>
                        <span className="text-[12px] text-foreground-500">
                          {FILA_ROTULO[fila.situacao] ?? ''}
                        </span>
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                )}
                <td className={`px-4 py-2.5 text-[14px] ${classeLinha}`}>{i.grupo || '—'}</td>
                <td className={`px-4 py-2.5 text-[14px] ${classeLinha}`}>
                  {i.participante_telefone || '—'}
                </td>
                <td className={`px-4 py-2.5 text-[14px] ${classeLinha}`}>
                  {formatarDataCurta(i.criado_em)}
                </td>
                <td className="px-4 py-2.5">
                  <SeloSituacao status={i.status} />
                </td>
                <td className={`px-4 py-2.5 text-right text-[14px] ${classeLinha}`}>
                  {formatarValor(i.valor_centavos)}
                </td>
                <td className={`px-4 py-2.5 text-right text-[14px] ${saldoCor}`}>
                  {saldo === null || suplente ? '—' : formatarDinheiro(saldo)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}